import { PointLight } from "@babylonjs/core/Lights/pointLight";
import type { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import type { Scene } from "@babylonjs/core/scene";
import { TORCH_FRAME_COUNT, TorchSpriteGenerator } from "../assets/TorchSpriteGenerator";
import { SeededRandom } from "../core/SeededRandom";
import {
  CELL_SIZE,
  TileType,
  manhattanDistance,
  type LevelData,
  type Point,
} from "../generation/types";
import type { MaterialFactory } from "./MaterialFactory";
import { restrictLightToNearbyMeshes } from "./LocalLightMeshSelector";
import { gridToWorld } from "./worldCoordinates";

const TORCH_HEIGHT = 2.05;
const TORCH_FRAME_SECONDS = 0.14;
const TORCH_SPACING_CELLS = 7;
export const MAX_DUNGEON_TORCHES = 20;

export function calculateTorchFlicker(elapsedSeconds: number, phase: number): number {
  return (
    0.82 +
    Math.sin(elapsedSeconds * 11.7 + phase) * 0.12 +
    Math.sin(elapsedSeconds * 27.3 + phase * 0.7) * 0.06
  );
}

const WALL_DIRECTIONS = [
  { x: 0, y: -1 },
  { x: 1, y: 0 },
  { x: 0, y: 1 },
  { x: -1, y: 0 },
] as const;

export interface DungeonTorchMount {
  readonly cell: Point;
  readonly wallDirection: Point;
}

interface AnimatedTorchLight {
  readonly light: PointLight;
  readonly phase: number;
  readonly baseIntensity: number;
}

export interface DungeonTorchViewTarget {
  readonly position: Vector3;
  readonly viewingPosition: Vector3;
}

export function selectDungeonTorchMounts(
  level: LevelData,
  maximum = MAX_DUNGEON_TORCHES,
): DungeonTorchMount[] {
  if (maximum <= 0) return [];
  const candidates: DungeonTorchMount[] = [];
  for (const cell of level.grid.walkableCells()) {
    const wallDirection = WALL_DIRECTIONS.find((direction) => {
      const wall = { x: cell.x + direction.x, y: cell.y + direction.y };
      return (
        wall.x >= 0 &&
        wall.y >= 0 &&
        wall.x < level.grid.width &&
        wall.y < level.grid.height &&
        level.grid.get(wall) === TileType.Wall
      );
    });
    if (wallDirection) candidates.push({ cell, wallDirection });
  }

  const selected: DungeonTorchMount[] = [];
  const startCandidates = candidates
    .filter((candidate) => manhattanDistance(candidate.cell, level.playerStart) <= 9)
    .sort(
      (first, second) =>
        manhattanDistance(first.cell, level.playerStart) -
        manhattanDistance(second.cell, level.playerStart),
    );
  addSpacedMounts(selected, startCandidates, Math.min(2, maximum), 4);

  const random = new SeededRandom(`${level.seed}:dungeon-torches`);
  addSpacedMounts(selected, random.shuffle(candidates), maximum, TORCH_SPACING_CELLS);
  return selected;
}

export class DungeonTorchSystem {
  private elapsed = 0;
  private frame = 0;
  private readonly texture: Texture;
  private readonly animatedLights: AnimatedTorchLight[] = [];
  private readonly viewTargets: DungeonTorchViewTarget[] = [];
  public readonly count: number;

  public constructor(scene: Scene, level: LevelData, materials: MaterialFactory) {
    const material = materials.createAtlasMaterial(
      "dungeon-wall-torch-material",
      new TorchSpriteGenerator(level.seed).createAtlas(),
    );
    this.texture = material.diffuseTexture as Texture;
    this.texture.uScale = 1 / TORCH_FRAME_COUNT;
    this.texture.vScale = 1;
    this.texture.uOffset = 0;

    const mounts = selectDungeonTorchMounts(level);
    this.count = mounts.length;
    mounts.forEach((mount, index) => {
      const cellWorld = gridToWorld(mount.cell, level.grid);
      const towardWall = new Vector3(mount.wallDirection.x, 0, mount.wallDirection.y);
      const inward = towardWall.scale(-1);
      const position = new Vector3(cellWorld.x, TORCH_HEIGHT, cellWorld.z).addInPlace(
        towardWall.scale(CELL_SIZE * 0.5 - 0.2),
      );
      const torch = MeshBuilder.CreatePlane(
        `dungeon-wall-torch-${index}`,
        { width: 0.9, height: 1.35 },
        scene,
      );
      torch.position.copyFrom(position);
      torch.lookAt(position.add(inward));
      torch.material = material;
      torch.isPickable = false;
      torch.metadata = { surface: "torch", animatedFrames: TORCH_FRAME_COUNT };
      this.viewTargets.push({
        position: position.clone(),
        viewingPosition: position.add(inward.scale(1.65)),
      });

      const light = new PointLight(
        `dungeon-torch-light-${index}`,
        position.add(inward.scale(0.9)).add(new Vector3(0, 0.24, 0)),
        scene,
      );
      light.diffuse = new Color3(1, 0.46, 0.13);
      light.specular = Color3.Black();
      light.range = 14;
      light.radius = 0.35;
      const baseIntensity = 2.55 + (index % 4) * 0.12;
      light.intensity = baseIntensity;
      restrictLightToNearbyMeshes(light, scene.meshes, light.position, light.range);
      this.animatedLights.push({ light, phase: index * 1.73, baseIntensity });
    });
  }

  public refreshReceivers(scene: Scene): void {
    for (const {light} of this.animatedLights) {
      restrictLightToNearbyMeshes(light, scene.meshes, light.position, light.range);
      for (const mesh of scene.meshes) {
        if (mesh.metadata?.enemyId && !light.includedOnlyMeshes.includes(mesh)) light.includedOnlyMeshes.push(mesh);
      }
    }
  }

  public update(deltaSeconds: number): void {
    this.elapsed += deltaSeconds;
    const nextFrame = Math.floor(this.elapsed / TORCH_FRAME_SECONDS) % TORCH_FRAME_COUNT;
    if (nextFrame !== this.frame) {
      this.frame = nextFrame;
      this.texture.uOffset = this.frame / TORCH_FRAME_COUNT;
    }
    for (const animated of this.animatedLights) {
      const flicker = calculateTorchFlicker(this.elapsed, animated.phase);
      animated.light.intensity = animated.baseIntensity * flicker;
    }
  }

  public getViewTarget(index = 0): DungeonTorchViewTarget | undefined {
    return this.viewTargets[index];
  }

  public get lightIntensities(): readonly number[] {
    return this.animatedLights.map((animated) => animated.light.intensity);
  }
}

function addSpacedMounts(
  selected: DungeonTorchMount[],
  candidates: readonly DungeonTorchMount[],
  targetCount: number,
  spacing: number,
): void {
  for (const candidate of candidates) {
    if (selected.length >= targetCount) return;
    if (selected.every((existing) => manhattanDistance(existing.cell, candidate.cell) >= spacing)) {
      selected.push(candidate);
    }
  }
}
